import { ref } from 'vue'
import ApiService from '@/services/api.service'
import type {
  DocumentPlaceholder,
  DocumentPlaceholderLabelsResponse,
  DocumentPlaceholderUpdate
} from '@/types/generated'

export function usePlaceholders() {
  const placeholders = ref<DocumentPlaceholder[]>([])
  const placeholder = ref<DocumentPlaceholder | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const fetchPlaceholders = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await ApiService.get('/document_placeholders/', {}, '/v3')
      placeholders.value = response.data
    } catch (err) {
      error.value = 'Failed to fetch placeholders'
      console.error('Error fetching placeholders:', err)
    } finally {
      isLoading.value = false
    }
  }

  const fetchPlaceholder = async (uuid: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await ApiService.get(`/document_placeholders/${uuid}/`, {}, '/v3')
      placeholder.value = response.data
    } catch (err) {
      error.value = 'Failed to fetch placeholder'
      console.error('Error fetching placeholder:', err)
    } finally {
      isLoading.value = false
    }
  }

  const updatePlaceholder = async (
    uuid: string,
    data: DocumentPlaceholderUpdate
  ): Promise<DocumentPlaceholder> => {
    error.value = null
    try {
      const response = await ApiService.patch(`/document_placeholders/${uuid}/`, data, {}, '/v3')
      const index = placeholders.value.findIndex((p) => p.uuid === uuid)
      if (index !== -1) {
        placeholders.value[index] = response.data
      }
      if (placeholder.value?.uuid === uuid) {
        placeholder.value = response.data
      }
      return response.data
    } catch (err) {
      error.value = 'Failed to update placeholder'
      throw err
    }
  }

  const deletePlaceholderById = async (uuid: string) => {
    error.value = null
    try {
      await ApiService.delete(`/document_placeholders/${uuid}/`, undefined, '/v3')
      placeholders.value = placeholders.value.filter((p) => p.uuid !== uuid)
      if (placeholder.value?.uuid === uuid) {
        placeholder.value = null
      }
    } catch (err) {
      error.value = 'Failed to delete placeholder'
      throw err
    }
  }

  const generateLabelsForPlaceholder = async (
    placeholderName: string
  ): Promise<DocumentPlaceholderLabelsResponse | null> => {
    error.value = null
    try {
      const response = await ApiService.post(
        '/document_placeholders/generate_labels/',
        {
          placeholder_name: placeholderName
        },
        {},
        '/v3'
      )
      return response.data
    } catch (err) {
      error.value = 'Failed to generate labels'
      throw err
    }
  }

  return {
    placeholders,
    placeholder,
    isLoading,
    error,
    fetchPlaceholders,
    fetchPlaceholder,
    updatePlaceholder,
    deletePlaceholderById,
    generateLabelsForPlaceholder
  }
}
